import type { AuthCtx, Citation, SearchHit, SearchScope } from "@gencow/core";
import { sql, type SQL } from "drizzle-orm";
import { rowsFromExecute, truncateSnippet } from "./server-grounding-common.js";

type GroundingDbLike = {
  execute(query: SQL): Promise<unknown>;
};

type CanonicalCitationRow = {
  chunk_id: string;
  chunk_text: string;
  source_id: string;
  source_title: string;
  section_id: string | null;
  section_path: unknown;
  chunk_page_start: number | null;
  chunk_page_end: number | null;
  section_page_start: number | null;
  section_page_end: number | null;
};

export async function buildCanonicalCitations(params: {
  db: GroundingDbLike;
  auth: AuthCtx;
  scope: SearchScope;
  hits: SearchHit[];
}): Promise<Citation[]> {
  const chunkIds = [...new Set(params.hits.map((hit) => String(hit.row.id ?? hit.id)))];
  if (chunkIds.length === 0) return [];

  const rows = await params.db.execute(buildCanonicalCitationQuery(params.scope, params.auth, chunkIds));
  const rowsByChunkId = new Map(
    rowsFromExecute<CanonicalCitationRow>(rows).map((row) => [row.chunk_id, row]),
  );

  const citations: Citation[] = [];
  for (const hit of params.hits) {
    const row = rowsByChunkId.get(String(hit.row.id ?? hit.id));
    if (!row) continue;

    citations.push({
      sourceId: row.source_id,
      sourceTitle: row.source_title,
      ...(row.section_id ? { sectionId: row.section_id } : {}),
      ...(toStringArray(row.section_path).length > 0 ? { sectionPath: toStringArray(row.section_path) } : {}),
      ...numberRange(
        row.chunk_page_start ?? row.section_page_start,
        row.chunk_page_end ?? row.section_page_end,
      ),
      chunkIds: [row.chunk_id],
      snippet: truncateSnippet(row.chunk_text),
      coverage: "context",
      confidence: 0,
    });
  }
  return citations;
}

function buildCanonicalCitationQuery(scope: SearchScope, auth: AuthCtx, chunkIds: string[]): SQL {
  return sql`
    SELECT
      c.id AS chunk_id,
      c.chunk_text AS chunk_text,
      c.source_id AS source_id,
      s.source_title AS source_title,
      sec.id AS section_id,
      sec.section_path AS section_path,
      c.page_start AS chunk_page_start,
      c.page_end AS chunk_page_end,
      sec.page_start AS section_page_start,
      sec.page_end AS section_page_end
    FROM rag_chunks c
    JOIN rag_sources s
      ON s.id = c.source_id
      AND s.corpus = c.corpus
      AND s.visibility_scope = c.visibility_scope
      AND COALESCE(s.owner_user_id, '') = COALESCE(c.owner_user_id, '')
    LEFT JOIN rag_sections sec
      ON sec.id = c.section_id
      AND sec.corpus = c.corpus
      AND sec.visibility_scope = c.visibility_scope
      AND COALESCE(sec.owner_user_id, '') = COALESCE(c.owner_user_id, '')
    WHERE c.id IN (${joinSql(
      chunkIds.map((chunkId) => sql`${chunkId}`),
      ", ",
    )})
      AND c.corpus = ${scope.corpus}
      AND c.visibility_scope = ${scope.visibility}
      AND ${buildOwnerClause(scope, auth)}
  `;
}

function joinSql(chunks: SQL[], separator: string): SQL {
  if (chunks.length === 0) {
    return sql`true`;
  }

  let current = chunks[0];
  for (const chunk of chunks.slice(1)) {
    current = sql`${current}${sql.raw(separator)}${chunk}`;
  }
  return current;
}

function buildOwnerClause(scope: SearchScope, auth: AuthCtx): SQL {
  if (scope.visibility === "private") {
    const identity = auth.requireAuth();
    const ownerUserId = scope.ownerUserId ?? identity.id;
    if (ownerUserId !== identity.id) {
      return sql`false`;
    }
    return sql`c.owner_user_id = ${ownerUserId}`;
  }

  if (scope.ownerUserId) {
    return sql`c.owner_user_id = ${scope.ownerUserId}`;
  }

  return sql`c.owner_user_id IS NULL`;
}

function numberRange(start: number | null, end: number | null): { pageStart?: number; pageEnd?: number } {
  return {
    ...(typeof start === "number" ? { pageStart: start } : {}),
    ...(typeof end === "number" ? { pageEnd: end } : {}),
  };
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}
